/**
 * VIP等级管理页面
 * 仅管理员可用：新增、编辑、删除VIP等级，自定义折扣率
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Tag, Space, Button, Modal, Form, Input, InputNumber,
  Select, Switch, message, Popconfirm, ColorPicker, Badge,
} from 'antd';
import {
  PlusOutlined, CrownOutlined, EditOutlined, DeleteOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { vipLevelApi } from '../services/api';

const VipLevelManage = () => {
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);

  // 弹窗
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vipLevelApi.list();
      setList(res.data || []);
    } catch { }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  // 新增
  const handleAdd = () => {
    setEditingLevel(null);
    form.resetFields();
    form.setFieldsValue({
      discount: 95, sortOrder: 0, isDefault: false, status: true,
      color: '#666',
    });
    setModalOpen(true);
  };

  // 编辑
  const handleEdit = (record) => {
    setEditingLevel(record);
    form.setFieldsValue({
      name: record.name,
      discount: record.discount,
      description: record.description,
      color: record.color,
      sortOrder: record.sort_order,
      isDefault: record.is_default === 1,
      status: record.status === 1,
    });
    setModalOpen(true);
  };

  // 保存
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const data = {
        name: values.name,
        discount: values.discount,
        description: values.description || '',
        color: typeof values.color === 'string' ? values.color : values.color?.toHexString?.() || '#666',
        sortOrder: values.sortOrder || 0,
        isDefault: !!values.isDefault,
        status: values.status !== false ? 1 : 0,
      };

      if (editingLevel) {
        await vipLevelApi.update(editingLevel.id, data);
        message.success('VIP等级已更新');
      } else {
        await vipLevelApi.create(data);
        message.success('VIP等级已创建');
      }

      setModalOpen(false);
      loadList();
    } catch (e) {
      if (e?.errorFields) return; // 表单校验失败
    }
    finally { setSaving(false); }
  };

  // 删除
  const handleDelete = async (id) => {
    try {
      await vipLevelApi.remove(id);
      message.success('VIP等级已删除');
      loadList();
    } catch { }
  };

  const columns = [
    {
      title: '排序', dataIndex: 'sort_order', width: 60,
      sorter: (a, b) => a.sort_order - b.sort_order,
    },
    {
      title: '等级标签', key: 'tag', width: 180,
      render: (_, r) => (
        <Tag color={r.status === 1 ? (r.color || '#666') : '#d9d9d9'}
          icon={<CrownOutlined />}
          style={{ fontSize: 14, padding: '2px 12px' }}
        >
          {r.name}
          {r.is_default === 1 && ' (默认)'}
        </Tag>
      ),
    },
    {
      title: '折扣率', dataIndex: 'discount', width: 150,
      render: (v) => {
        if (v >= 100) return <span style={{ color: '#999' }}>原价（{v}%）</span>;
        const color = v >= 90 ? '#52c41a' : v >= 80 ? '#faad14' : '#f5222d';
        return (
          <Space>
            <span style={{ fontWeight: 'bold', color, fontSize: 16 }}>
              {v / 10}折
            </span>
            <span style={{ color: '#999', fontSize: 12 }}>（{v}%）</span>
          </Space>
        );
      },
      sorter: (a, b) => a.discount - b.discount,
    },
    {
      title: '描述', dataIndex: 'description', ellipsis: true,
      render: (v) => v || '-',
    },
    {
      title: '颜色', dataIndex: 'color', width: 80,
      render: (v) => (
        <Space>
          <div style={{
            width: 20, height: 20, borderRadius: 4,
            background: v || '#666', border: '1px solid #ddd',
          }} />
          <span style={{ fontSize: 12 }}>{v || '-'}</span>
        </Space>
      ),
    },
    {
      title: '客户数', dataIndex: 'user_count', width: 80,
      sorter: (a, b) => (a.user_count || 0) - (b.user_count || 0),
    },
    {
      title: '状态', dataIndex: 'status', width: 80,
      render: (v) => (
        <Badge status={v === 1 ? 'success' : 'error'}
          text={v === 1 ? '启用' : '禁用'} />
      ),
    },
    {
      title: '操作', key: 'actions', width: 180, fixed: 'right',
      render: (_, r) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />}
            onClick={() => handleEdit(r)}>编辑</Button>
          <Popconfirm
            title={r.user_count > 0
              ? `该等级下有 ${r.user_count} 位客户，无法删除`
              : '确定删除该VIP等级？'}
            disabled={r.user_count > 0}
            onConfirm={() => handleDelete(r.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />}
              disabled={r.user_count > 0}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title={
          <Space>
            <CrownOutlined style={{ color: '#faad14' }} />
            VIP等级管理
            <span style={{ fontSize: 12, color: '#999', fontWeight: 'normal' }}>
              - 定义折扣规则，维修报价和销售下单时自动计算优惠价
            </span>
          </Space>
        }
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            新增等级
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={list}
          loading={loading}
          pagination={false}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* VIP等级编辑弹窗 */}
      <Modal
        title={
          <Space>
            <CrownOutlined style={{ color: '#faad14' }} />
            {editingLevel ? `编辑 - ${editingLevel.name}` : '新增VIP等级'}
          </Space>
        }
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        confirmLoading={saving}
        okText="保存"
        cancelText="取消"
        destroyOnClose
        width={520}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name" label="等级名称"
            rules={[{ required: true, message: '请输入等级名称' }]}
          >
            <Input placeholder="如：金卡会员、VIP1..." maxLength={20} />
          </Form.Item>

          <Form.Item
            name="discount" label="折扣率（%）"
            rules={[
              { required: true, message: '请输入折扣率' },
              { type: 'number', min: 1, max: 100, message: '折扣率须在1-100之间' },
            ]}
            extra="100 = 原价无折扣，90 = 9折，85 = 8.5折，以此类推"
          >
            <InputNumber
              min={1} max={100}
              style={{ width: '100%' }}
              addonAfter="%"
              placeholder="95"
            />
          </Form.Item>

          <Form.Item name="description" label="等级描述">
            <Input.TextArea rows={2} placeholder="描述该等级的特点和权益..."
              maxLength={200} showCount />
          </Form.Item>

          <Space size="large" wrap>
            <Form.Item name="color" label="标签颜色" style={{ marginBottom: 0 }}>
              <ColorPicker format="hex" />
            </Form.Item>

            <Form.Item name="sortOrder" label="排序权重" style={{ marginBottom: 0 }}
              extra="数值越小越靠前">
              <InputNumber min={0} max={999} style={{ width: 100 }} />
            </Form.Item>
          </Space>

          <Space size="large" wrap style={{ marginTop: 16 }}>
            <Form.Item name="isDefault" label="设为默认等级" valuePropName="checked"
              style={{ marginBottom: 0 }}
              extra="新客户自动分配此等级">
              <Switch />
            </Form.Item>

            <Form.Item name="status" label="启用" valuePropName="checked"
              style={{ marginBottom: 0 }}
              extra="关闭后该等级将不生效">
              <Switch />
            </Form.Item>
          </Space>
        </Form>

        {/* 折扣预览 */}
        {(() => {
          const disc = form.getFieldValue('discount');
          if (disc && disc < 100) {
            return (
              <div style={{
                background: '#f6ffed', border: '1px solid #b7eb8f',
                borderRadius: 6, padding: '12px 16px', marginTop: 16,
              }}>
                <Space>
                  <QuestionCircleOutlined style={{ color: '#52c41a' }} />
                  <span style={{ color: '#52c41a' }}>
                    客户原价¥100.00，实付 <strong>¥{(100 * disc / 100).toFixed(2)}</strong>，
                    优惠 <strong>¥{(100 - 100 * disc / 100).toFixed(2)}</strong>
                  </span>
                </Space>
              </div>
            );
          }
          return null;
        })()}
      </Modal>
    </div>
  );
};

export default VipLevelManage;
